// Author: Ahsan Saleem
/**
 * Toast — transient confirmation that an operation happened ("no dead clicks").
 * Any code calls `toast(text, tone)`; the Toaster (mounted once in the Shell)
 * renders a short-lived stack. This is how BloomOS acknowledges a command run from
 * anywhere — the palette, an object action — without stealing the operator's context.
 */
import { useEffect, useState } from 'react';
import { Check, TriangleAlert, Info, X } from 'lucide-react';

export type ToastTone = 'success' | 'error' | 'info';
interface ToastMsg { id: number; text: string; tone: ToastTone; }

let seq = 0;
export function toast(text: string, tone: ToastTone = 'success'): void {
  window.dispatchEvent(new CustomEvent('bloomos-toast', { detail: { id: ++seq, text, tone } }));
}

const ICON: Record<ToastTone, typeof Check> = { success: Check, error: TriangleAlert, info: Info };

export function Toaster() {
  const [items, setItems] = useState<ToastMsg[]>([]);
  useEffect(() => {
    const on = (e: Event) => {
      const t = (e as CustomEvent<ToastMsg>).detail;
      setItems((cur) => [...cur, t].slice(-4));
      window.setTimeout(() => setItems((cur) => cur.filter((x) => x.id !== t.id)), 4200);
    };
    window.addEventListener('bloomos-toast', on);
    return () => window.removeEventListener('bloomos-toast', on);
  }, []);
  const dismiss = (id: number) => setItems((cur) => cur.filter((x) => x.id !== id));
  if (!items.length) return null;
  return (
    <div className="toaster" role="status" aria-live="polite">
      {items.map((t) => {
        const Icon = ICON[t.tone];
        return (
          <div key={t.id} className={`toast toast-${t.tone}`}>
            <Icon size={16} aria-hidden />
            <span className="toast-text">{t.text}</span>
            <button className="toast-x" onClick={() => dismiss(t.id)} aria-label="Dismiss"><X size={14} aria-hidden /></button>
          </div>
        );
      })}
    </div>
  );
}
